import { useState } from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Building2, MapPin, CheckCircle2, AlertCircle } from 'lucide-react';
import { TRN, Branch } from '../types';

interface TRNBranchSelectorProps {
  trns: TRN[];
  onSelect: (trnId: string, branchId: string) => void;
}

export default function TRNBranchSelector({ trns, onSelect }: TRNBranchSelectorProps) {
  const [selectedTRNId, setSelectedTRNId] = useState<string>('');
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  
  const selectedTRN = trns.find(t => t.id === selectedTRNId);
  const availableBranches = selectedTRN?.branches || [];

  const handleContinue = () => {
    if (selectedTRNId && selectedBranchId) {
      onSelect(selectedTRNId, selectedBranchId);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      </div>

      <Card className="w-full max-w-2xl premium-shadow-lg border-0 relative z-10">
        <CardHeader className="space-y-3 pb-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#0052CC] to-indigo-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl">Select Your Tax Registration & Branch</CardTitle>
              <CardDescription>Choose your TRN and branch to continue</CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6 pt-6">
          {/* TRN Selection */}
          <div className="space-y-3">
            <label className="block text-sm text-gray-700">
              Tax Registration Number (TRN) <span className="text-red-500">*</span>
            </label>
            <Select value={selectedTRNId} onValueChange={(value) => {
              setSelectedTRNId(value);
              setSelectedBranchId(''); // Reset branch when TRN changes
            }}>
              <SelectTrigger className="h-12 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20">
                <SelectValue placeholder="Select Tax Registration Number" />
              </SelectTrigger>
              <SelectContent>
                {trns.map((trn) => (
                  <SelectItem key={trn.id} value={trn.id}>
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span>{trn.trnNumber}</span>
                          {trn.status === 'active' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-orange-500" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{trn.legalEntityName}</p>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedTRN && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-[#0052CC] mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{selectedTRN.legalEntityName}</p>
                    <p className="text-xs text-gray-600">Registered: {new Date(selectedTRN.registrationDate).toLocaleDateString('en-AE')}</p>
                    <p className="text-xs text-gray-600">Available Branches: {selectedTRN.branches.length}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Branch Selection */}
          <div className="space-y-3">
            <label className="block text-sm text-gray-700">
              Branch <span className="text-red-500">*</span>
            </label>
            <Select 
              value={selectedBranchId} 
              onValueChange={setSelectedBranchId}
              disabled={!selectedTRNId}
            >
              <SelectTrigger className="h-12 border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20">
                <SelectValue placeholder={selectedTRNId ? "Select Branch" : "Select TRN first"} />
              </SelectTrigger>
              <SelectContent>
                {availableBranches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id} disabled={branch.status !== 'active'}>
                    <div className="flex items-center gap-3 py-1">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span>{branch.branchName}</span>
                          <span className="text-xs text-gray-500">({branch.branchCode})</span>
                          {branch.status === 'active' ? (
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                          ) : (
                            <AlertCircle className="w-3 h-3 text-orange-500" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{branch.city}, {branch.emirate}</p>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedBranchId && availableBranches.find(b => b.id === selectedBranchId) && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    {(() => {
                      const branch = availableBranches.find(b => b.id === selectedBranchId)!;
                      return (
                        <>
                          <p className="text-sm text-gray-900">{branch.branchName}</p>
                          <p className="text-xs text-gray-600">{branch.address}</p>
                          <p className="text-xs text-gray-600">{branch.city}, {branch.emirate}</p>
                          {branch.manager && (
                            <p className="text-xs text-gray-600">Manager: {branch.manager}</p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="pt-4 border-t">
            <Button
              onClick={handleContinue}
              disabled={!selectedTRNId || !selectedBranchId}
              className="w-full h-12 bg-gradient-to-r from-[#0052CC] to-indigo-600 hover:from-[#0041A3] hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all"
            >
              Continue to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
